// Yetkazib beruvchi va xarid — xsib-finance `/api/finance/suppliers`, `/purchases`,
// `/purchase-receiving`. Summalar minor birlikda, butun son. Server `non_null` bilan
// javob beradi, shuning uchun bo'sh maydonlar `.nullish()`.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { instantSchema, uuidSchema } from '@/shared/api/schemas'
import { money, type Money } from '@/shared/lib/money'
import type { PageResult } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'

export const SUPPLIER_CATEGORIES = [
  'BANDWIDTH',
  'NETWORK_EQUIPMENT',
  'IT_EQUIPMENT',
  'FIBER_CABLING',
  'TOWER_EQUIPMENT',
  'SOFTWARE_LICENSING',
  'OFFICE_SUPPLIES',
  'MAINTENANCE_SERVICES',
  'OTHER',
] as const
export type SupplierCategory = (typeof SUPPLIER_CATEGORIES)[number]

export const PURCHASE_STATES = [
  'DRAFT',
  'SUBMITTED',
  'HQ_VERIFIED',
  'PARTIALLY_RECEIVED',
  'RECEIVED',
  'CANCELLED',
] as const
export type PurchaseState = (typeof PURCHASE_STATES)[number]

export const PAYMENT_STATUSES = ['UNPAID', 'PARTIALLY_PAID', 'PAID', 'OVERDUE'] as const
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number]

export const LINE_KINDS = ['STOCK', 'SERVICE'] as const
export type LineKind = (typeof LINE_KINDS)[number]

export const ATTACHMENT_KINDS = ['CONTRACT', 'INVOICE', 'DELIVERY_NOTE', 'PAYMENT_PROOF', 'OTHER'] as const
export type AttachmentKind = (typeof ATTACHMENT_KINDS)[number]
export const SUPPLIER_ATTACHMENT_KINDS: readonly AttachmentKind[] = ['CONTRACT', 'OTHER']
export const PURCHASE_ATTACHMENT_KINDS: readonly AttachmentKind[] = ['INVOICE', 'OTHER']

export const ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024
export const ATTACHMENT_ACCEPT = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp', '.docx', '.xlsx'].join(',')

/** Bosh ofis tasdiqlagan, ya'ni to'lov va kirim mumkin bo'lgan holatlar */
export const VERIFIED_STATES: readonly PurchaseState[] = ['HQ_VERIFIED', 'PARTIALLY_RECEIVED', 'RECEIVED']

const actorSchema = z.object({ id: uuidSchema, name: z.string().nullish() })
const refSchema = z.object({ id: uuidSchema, code: z.string().nullish(), name: z.string().nullish() })
const pageOf = <T extends z.ZodTypeAny>(row: T) =>
  z.object({ rows: z.array(row), total: z.number(), page: z.number(), size: z.number() })

export interface ActorRef {
  id: string
  name: string
}

export interface NodeRef {
  id: string
  code: string
  name: string
}

const actorOf = (raw: z.infer<typeof actorSchema>): ActorRef => ({ id: raw.id, name: raw.name ?? raw.id })
const toActor = (raw: z.infer<typeof actorSchema> | null | undefined): ActorRef | null => (raw ? actorOf(raw) : null)
const toRef = (raw: z.infer<typeof refSchema> | null | undefined): NodeRef | null =>
  raw ? { id: raw.id, code: raw.code ?? '', name: raw.name ?? '' } : null

// --- Yetkazib beruvchi ---------------------------------------------------------

const supplierRowSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
  category: z.enum(SUPPLIER_CATEGORIES),
  contactPerson: z.string().nullish(),
  phone: z.string().nullish(),
  active: z.boolean(),
  outstandingMinor: z.number(),
  overdueMinor: z.number(),
  currency: z.string(),
})

export interface SupplierRow {
  id: string
  code: string
  name: string
  category: SupplierCategory
  contactPerson: string | null
  phone: string | null
  active: boolean
  outstanding: Money
  overdue: Money
}

const supplierSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
  category: z.enum(SUPPLIER_CATEGORIES),
  contactPerson: z.string().nullish(),
  phone: z.string().nullish(),
  whatsapp: z.string().nullish(),
  email: z.string().nullish(),
  address: z.string().nullish(),
  taxId: z.string().nullish(),
  bankDetails: z.string().nullish(),
  contractRef: z.string().nullish(),
  paymentTermsDays: z.number(),
  creditLimitMinor: z.number().nullish(),
  active: z.boolean(),
  notes: z.string().nullish(),
  createdBy: actorSchema,
  createdAt: instantSchema,
  updatedBy: actorSchema.nullish(),
  updatedAt: instantSchema,
})

export interface Supplier {
  id: string
  code: string
  name: string
  category: SupplierCategory
  contactPerson: string | null
  phone: string | null
  whatsapp: string | null
  email: string | null
  address: string | null
  taxId: string | null
  bankDetails: string | null
  contractRef: string | null
  paymentTermsDays: number
  creditLimitMinor: number | null
  active: boolean
  notes: string | null
  createdBy: ActorRef
  createdAt: string
  updatedBy: ActorRef | null
  updatedAt: string
}

function toSupplier(raw: z.infer<typeof supplierSchema>): Supplier {
  return {
    ...raw,
    contactPerson: raw.contactPerson ?? null,
    phone: raw.phone ?? null,
    whatsapp: raw.whatsapp ?? null,
    email: raw.email ?? null,
    address: raw.address ?? null,
    taxId: raw.taxId ?? null,
    bankDetails: raw.bankDetails ?? null,
    contractRef: raw.contractRef ?? null,
    creditLimitMinor: raw.creditLimitMinor ?? null,
    notes: raw.notes ?? null,
    createdBy: actorOf(raw.createdBy),
    updatedBy: toActor(raw.updatedBy),
  }
}

export interface SupplierFilter {
  category: SupplierCategory | null
  active: boolean | null
}

export function fetchSupplierPage(
  filter: SupplierFilter,
): (state: TableState, signal: AbortSignal) => Promise<PageResult<SupplierRow>> {
  return async (table, signal) => {
    const response = await api.get<unknown>('/api/finance/suppliers', {
      signal,
      params: {
        ...(table.search ? { search: table.search } : {}),
        ...(filter.category ? { category: filter.category } : {}),
        ...(filter.active === null ? {} : { active: filter.active }),
        page: table.page - 1,
        size: table.pageSize,
      },
    })
    const parsed = pageOf(supplierRowSchema).parse(response.data)
    return {
      items: parsed.rows.map((raw) => ({
        id: raw.id,
        code: raw.code,
        name: raw.name,
        category: raw.category,
        contactPerson: raw.contactPerson ?? null,
        phone: raw.phone ?? null,
        active: raw.active,
        outstanding: money(raw.outstandingMinor, raw.currency),
        overdue: money(raw.overdueMinor, raw.currency),
      })),
      total: parsed.total,
    }
  }
}

/** Tanlagichlar uchun — faol yetkazib beruvchilar (bir sahifa, eng ko'pi 200) */
export async function fetchActiveSuppliers(signal?: AbortSignal): Promise<readonly { id: string; code: string; name: string }[]> {
  const response = await api.get<unknown>('/api/finance/suppliers', {
    ...(signal ? { signal } : {}),
    params: { active: true, page: 0, size: 200 },
  })
  return pageOf(supplierRowSchema)
    .parse(response.data)
    .rows.map((raw) => ({ id: raw.id, code: raw.code, name: raw.name }))
}

export async function fetchSupplier(id: string, signal?: AbortSignal): Promise<Supplier> {
  const response = await api.get<unknown>(`/api/finance/suppliers/${id}`, { ...(signal ? { signal } : {}) })
  return toSupplier(supplierSchema.parse(response.data))
}

export interface SupplierDraft {
  name: string
  category: SupplierCategory
  contactPerson: string | null
  phone: string | null
  whatsapp: string | null
  email: string | null
  address: string | null
  taxId: string | null
  bankDetails: string | null
  contractRef: string | null
  paymentTermsDays: number
  creditLimitMinor: number | null
  active: boolean
  notes: string | null
}

export async function saveSupplier(id: string | null, draft: SupplierDraft): Promise<Supplier> {
  const response = id
    ? await api.put<unknown>(`/api/finance/suppliers/${id}`, draft)
    : await api.post<unknown>('/api/finance/suppliers', draft)
  return toSupplier(supplierSchema.parse(response.data))
}

const summarySchema = z.object({
  currency: z.string(),
  purchaseCount: z.number(),
  totalPurchasesMinor: z.number(),
  recognizedMinor: z.number(),
  totalPaidMinor: z.number(),
  outstandingMinor: z.number(),
  overdueMinor: z.number(),
  awaitingVerification: z.number(),
  lastPurchaseOn: z.string().nullish(),
  lastPaymentOn: z.string().nullish(),
  creditLimitMinor: z.number().nullish(),
})

export interface SupplierSummary {
  purchaseCount: number
  totalPurchases: Money
  recognized: Money
  totalPaid: Money
  outstanding: Money
  overdue: Money
  awaitingVerification: number
  lastPurchaseOn: string | null
  lastPaymentOn: string | null
  creditLimit: Money | null
}

export async function fetchSupplierSummary(id: string, signal?: AbortSignal): Promise<SupplierSummary> {
  const response = await api.get<unknown>(`/api/finance/suppliers/${id}/summary`, { ...(signal ? { signal } : {}) })
  const raw = summarySchema.parse(response.data)
  const of = (amount: number) => money(amount, raw.currency)
  return {
    purchaseCount: raw.purchaseCount,
    totalPurchases: of(raw.totalPurchasesMinor),
    recognized: of(raw.recognizedMinor),
    totalPaid: of(raw.totalPaidMinor),
    outstanding: of(raw.outstandingMinor),
    overdue: of(raw.overdueMinor),
    awaitingVerification: raw.awaitingVerification,
    lastPurchaseOn: raw.lastPurchaseOn ?? null,
    lastPaymentOn: raw.lastPaymentOn ?? null,
    creditLimit: raw.creditLimitMinor === null || raw.creditLimitMinor === undefined ? null : of(raw.creditLimitMinor),
  }
}

const ledgerRowSchema = z.object({
  date: z.string(),
  type: z.enum(['PURCHASE', 'PAYMENT']),
  purchaseId: uuidSchema,
  purchaseNumber: z.string(),
  reference: z.string().nullish(),
  paymentId: uuidSchema.nullish(),
  purchaseMinor: z.number(),
  paymentMinor: z.number(),
  balanceMinor: z.number(),
})

export type SupplierLedgerRow = Omit<z.infer<typeof ledgerRowSchema>, 'reference' | 'paymentId'> & {
  reference: string | null
  paymentId: string | null
}

export async function fetchSupplierLedger(id: string, signal?: AbortSignal): Promise<SupplierLedgerRow[]> {
  const response = await api.get<unknown>(`/api/finance/suppliers/${id}/ledger`, { ...(signal ? { signal } : {}) })
  return z
    .array(ledgerRowSchema)
    .parse(response.data)
    .map((raw) => ({ ...raw, reference: raw.reference ?? null, paymentId: raw.paymentId ?? null }))
}

// --- Xarid ---------------------------------------------------------------------

const purchaseRowSchema = z.object({
  id: uuidSchema,
  number: z.string(),
  supplier: refSchema,
  invoiceNo: z.string().nullish(),
  invoiceDate: z.string(),
  dueDate: z.string().nullish(),
  orgNode: refSchema.nullish(),
  warehouse: refSchema.nullish(),
  state: z.enum(PURCHASE_STATES),
  currency: z.string(),
  totalMinor: z.number(),
  recognizedMinor: z.number(),
  paidMinor: z.number(),
  remainingMinor: z.number(),
  paymentStatus: z.enum(PAYMENT_STATUSES).nullish(),
  createdBy: actorSchema,
  createdAt: instantSchema,
})

export interface PurchaseRow {
  id: string
  number: string
  supplier: NodeRef
  invoiceNo: string | null
  invoiceDate: string
  dueDate: string | null
  orgNode: NodeRef | null
  warehouse: NodeRef | null
  state: PurchaseState
  currency: string
  total: Money
  recognized: Money
  paid: Money
  remaining: Money
  paymentStatus: PaymentStatus | null
  createdBy: ActorRef
  createdAt: string
}

function toPurchaseRow(raw: z.infer<typeof purchaseRowSchema>): PurchaseRow {
  const of = (amount: number) => money(amount, raw.currency)
  return {
    id: raw.id,
    number: raw.number,
    supplier: toRef(raw.supplier) ?? { id: raw.supplier.id, code: '', name: '' },
    invoiceNo: raw.invoiceNo ?? null,
    invoiceDate: raw.invoiceDate,
    dueDate: raw.dueDate ?? null,
    orgNode: toRef(raw.orgNode),
    warehouse: toRef(raw.warehouse),
    state: raw.state,
    currency: raw.currency,
    total: of(raw.totalMinor),
    recognized: of(raw.recognizedMinor),
    paid: of(raw.paidMinor),
    remaining: of(raw.remainingMinor),
    paymentStatus: raw.paymentStatus ?? null,
    createdBy: actorOf(raw.createdBy),
    createdAt: raw.createdAt,
  }
}

const lineSchema = z.object({
  id: uuidSchema,
  lineNo: z.number(),
  kind: z.enum(LINE_KINDS),
  stockItemId: uuidSchema.nullish(),
  accountId: uuidSchema.nullish(),
  accountCode: z.string().nullish(),
  accountName: z.string().nullish(),
  description: z.string(),
  quantity: z.number(),
  unitPriceMinor: z.number(),
  amountMinor: z.number(),
  receivedQuantity: z.number(),
})

export interface PurchaseLine {
  id: string
  lineNo: number
  kind: LineKind
  stockItemId: string | null
  accountId: string | null
  account: { code: string; name: string } | null
  description: string
  quantity: number
  unitPrice: Money
  amount: Money
  receivedQuantity: number
}

const purchaseSchema = z.object({
  purchase: purchaseRowSchema,
  memo: z.string().nullish(),
  submittedBy: actorSchema.nullish(),
  submittedAt: instantSchema.nullish(),
  verifiedBy: actorSchema.nullish(),
  verifiedAt: instantSchema.nullish(),
  verificationNote: z.string().nullish(),
  verificationEntryId: uuidSchema.nullish(),
  cancelledBy: actorSchema.nullish(),
  cancelledAt: instantSchema.nullish(),
  cancelReason: z.string().nullish(),
  lines: z.array(lineSchema),
})

export interface Purchase extends PurchaseRow {
  memo: string | null
  submittedBy: ActorRef | null
  submittedAt: string | null
  verifiedBy: ActorRef | null
  verifiedAt: string | null
  verificationNote: string | null
  cancelledBy: ActorRef | null
  cancelledAt: string | null
  cancelReason: string | null
  lines: PurchaseLine[]
}

function toPurchase(raw: z.infer<typeof purchaseSchema>): Purchase {
  const row = toPurchaseRow(raw.purchase)
  return {
    ...row,
    memo: raw.memo ?? null,
    submittedBy: toActor(raw.submittedBy),
    submittedAt: raw.submittedAt ?? null,
    verifiedBy: toActor(raw.verifiedBy),
    verifiedAt: raw.verifiedAt ?? null,
    verificationNote: raw.verificationNote ?? null,
    cancelledBy: toActor(raw.cancelledBy),
    cancelledAt: raw.cancelledAt ?? null,
    cancelReason: raw.cancelReason ?? null,
    lines: raw.lines.map((line) => ({
      id: line.id,
      lineNo: line.lineNo,
      kind: line.kind,
      stockItemId: line.stockItemId ?? null,
      accountId: line.accountId ?? null,
      account: line.accountCode ? { code: line.accountCode, name: line.accountName ?? '' } : null,
      description: line.description,
      quantity: line.quantity,
      unitPrice: money(line.unitPriceMinor, row.currency),
      amount: money(line.amountMinor, row.currency),
      receivedQuantity: line.receivedQuantity,
    })),
  }
}

export interface PurchaseFilter {
  supplierId: string | null
  state: PurchaseState | null
  overdueOnly: boolean
}

export function fetchPurchasePage(
  filter: PurchaseFilter,
): (state: TableState, signal: AbortSignal) => Promise<PageResult<PurchaseRow>> {
  return async (table, signal) => {
    const response = await api.get<unknown>('/api/finance/purchases', {
      signal,
      params: {
        ...(filter.supplierId ? { supplierId: filter.supplierId } : {}),
        ...(filter.state ? { state: filter.state } : {}),
        ...(filter.overdueOnly ? { overdueOnly: true } : {}),
        ...(table.search ? { search: table.search } : {}),
        page: table.page - 1,
        size: table.pageSize,
      },
    })
    const parsed = pageOf(purchaseRowSchema).parse(response.data)
    return { items: parsed.rows.map(toPurchaseRow), total: parsed.total }
  }
}

export async function fetchPurchase(id: string, signal?: AbortSignal): Promise<Purchase> {
  const response = await api.get<unknown>(`/api/finance/purchases/${id}`, { ...(signal ? { signal } : {}) })
  return toPurchase(purchaseSchema.parse(response.data))
}

export interface PurchaseLineDraft {
  kind: LineKind
  stockItemId: string | null
  accountId: string | null
  description: string
  quantity: number
  unitPriceMinor: number
}

export interface PurchaseDraft {
  supplierId: string
  invoiceNo: string | null
  invoiceDate: string
  dueDate: string | null
  orgNodeId: string | null
  warehouseNodeId: string | null
  memo: string | null
  lines: PurchaseLineDraft[]
}

export async function savePurchase(id: string | null, draft: PurchaseDraft): Promise<Purchase> {
  const response = id
    ? await api.put<unknown>(`/api/finance/purchases/${id}`, draft)
    : await api.post<unknown>('/api/finance/purchases', draft)
  return toPurchase(purchaseSchema.parse(response.data))
}

export type PurchaseDecision = 'submit' | 'verify' | 'cancel'

export async function decidePurchase(id: string, decision: PurchaseDecision, text: string | null): Promise<Purchase> {
  const response = await api.post<unknown>(
    `/api/finance/purchases/${id}/${decision}`,
    decision === 'submit' ? undefined : { text },
  )
  return toPurchase(purchaseSchema.parse(response.data))
}

/** Satr summasi — serverdagi `quantity * unit_price_minor` bilan bir xil */
export function lineAmountMinor(quantity: number | null | undefined, unitPriceMinor: number | null | undefined): number {
  return (quantity ?? 0) * (unitPriceMinor ?? 0)
}

// --- Kirim (omborchi) ----------------------------------------------------------

const receivingSchema = z.object({
  id: uuidSchema,
  number: z.string(),
  supplierName: z.string(),
  invoiceNo: z.string().nullish(),
  invoiceDate: z.string(),
  warehouse: refSchema,
  state: z.enum(PURCHASE_STATES),
  verifiedBy: actorSchema.nullish(),
  verifiedAt: instantSchema.nullish(),
  lines: z.array(
    z.object({
      id: uuidSchema,
      lineNo: z.number(),
      stockItemId: uuidSchema,
      description: z.string(),
      quantity: z.number(),
      receivedQuantity: z.number(),
    }),
  ),
})

export interface ReceivingLine {
  id: string
  lineNo: number
  stockItemId: string
  description: string
  quantity: number
  receivedQuantity: number
  remainingQuantity: number
}

export interface Receiving {
  id: string
  number: string
  supplierName: string
  invoiceNo: string | null
  invoiceDate: string
  warehouse: NodeRef
  state: PurchaseState
  verifiedBy: ActorRef | null
  verifiedAt: string | null
  lines: ReceivingLine[]
}

export async function fetchReceivingQueue(signal?: AbortSignal): Promise<Receiving[]> {
  const response = await api.get<unknown>('/api/finance/purchase-receiving', { ...(signal ? { signal } : {}) })
  return z
    .array(receivingSchema)
    .parse(response.data)
    .map((raw) => ({
      id: raw.id,
      number: raw.number,
      supplierName: raw.supplierName,
      invoiceNo: raw.invoiceNo ?? null,
      invoiceDate: raw.invoiceDate,
      warehouse: toRef(raw.warehouse) ?? { id: raw.warehouse.id, code: '', name: '' },
      state: raw.state,
      verifiedBy: toActor(raw.verifiedBy),
      verifiedAt: raw.verifiedAt ?? null,
      lines: raw.lines.map((line) => ({ ...line, remainingQuantity: line.quantity - line.receivedQuantity })),
    }))
}

const receiptSchema = z.object({
  id: uuidSchema,
  purchaseId: uuidSchema,
  warehouse: refSchema.nullish(),
  receivedBy: actorSchema,
  receivedAt: instantSchema,
  note: z.string().nullish(),
  entryId: uuidSchema.nullish(),
  amountMinor: z.number(),
  lines: z.array(
    z.object({
      purchaseLineId: uuidSchema,
      description: z.string(),
      quantity: z.number(),
      amountMinor: z.number(),
      stockMovementId: uuidSchema,
    }),
  ),
})

export type PurchaseReceipt = z.infer<typeof receiptSchema>

export interface ReceiptLineDraft {
  purchaseLineId: string
  quantity: number
}

export async function receivePurchase(
  id: string,
  lines: ReceiptLineDraft[],
  note: string | null,
  idempotencyKey: string,
): Promise<PurchaseReceipt> {
  const response = await api.post<unknown>(
    `/api/finance/purchase-receiving/${id}/receipts`,
    { lines, note },
    { headers: { 'Idempotency-Key': idempotencyKey } },
  )
  return receiptSchema.parse(response.data)
}

export async function fetchPurchaseReceipts(id: string, signal?: AbortSignal): Promise<PurchaseReceipt[]> {
  const response = await api.get<unknown>(`/api/finance/purchases/${id}/receipts`, { ...(signal ? { signal } : {}) })
  return z.array(receiptSchema).parse(response.data)
}

// --- To'lov ---------------------------------------------------------------------

const paymentSchema = z.object({
  id: uuidSchema,
  purchaseId: uuidSchema,
  purchaseNumber: z.string(),
  amountMinor: z.number(),
  currency: z.string(),
  paidOn: z.string(),
  account: refSchema,
  reference: z.string().nullish(),
  note: z.string().nullish(),
  entryId: uuidSchema,
  paidBy: actorSchema,
  createdAt: instantSchema,
})

export interface SupplierPayment {
  id: string
  purchaseId: string
  purchaseNumber: string
  amount: Money
  paidOn: string
  account: NodeRef
  reference: string | null
  note: string | null
  paidBy: ActorRef
  createdAt: string
}

function toPayment(raw: z.infer<typeof paymentSchema>): SupplierPayment {
  return {
    id: raw.id,
    purchaseId: raw.purchaseId,
    purchaseNumber: raw.purchaseNumber,
    amount: money(raw.amountMinor, raw.currency),
    paidOn: raw.paidOn,
    account: toRef(raw.account) ?? { id: raw.account.id, code: '', name: '' },
    reference: raw.reference ?? null,
    note: raw.note ?? null,
    paidBy: actorOf(raw.paidBy),
    createdAt: raw.createdAt,
  }
}

export async function fetchPurchasePayments(id: string, signal?: AbortSignal): Promise<SupplierPayment[]> {
  const response = await api.get<unknown>(`/api/finance/purchases/${id}/payments`, { ...(signal ? { signal } : {}) })
  return z.array(paymentSchema).parse(response.data).map(toPayment)
}

export interface PaymentDraft {
  amountMinor: number
  paidOn: string
  accountId: string
  reference: string | null
  note: string | null
}

export async function paySupplier(purchaseId: string, draft: PaymentDraft, idempotencyKey: string): Promise<SupplierPayment> {
  const response = await api.post<unknown>(`/api/finance/purchases/${purchaseId}/payments`, draft, {
    headers: { 'Idempotency-Key': idempotencyKey },
  })
  return toPayment(paymentSchema.parse(response.data))
}

// --- Ilovalar -------------------------------------------------------------------

const attachmentSchema = z.object({
  id: uuidSchema,
  kind: z.enum(ATTACHMENT_KINDS),
  purchaseId: uuidSchema.nullish(),
  purchaseNumber: z.string().nullish(),
  paymentId: uuidSchema.nullish(),
  fileName: z.string().nullish(),
  contentType: z.string(),
  sizeBytes: z.number(),
  createdBy: actorSchema,
  createdAt: instantSchema,
})

export interface SupplierAttachment {
  id: string
  kind: AttachmentKind
  purchaseNumber: string | null
  fileName: string | null
  sizeBytes: number
  createdBy: ActorRef
  createdAt: string
}

function toAttachment(raw: z.infer<typeof attachmentSchema>): SupplierAttachment {
  return {
    id: raw.id,
    kind: raw.kind,
    purchaseNumber: raw.purchaseNumber ?? null,
    fileName: raw.fileName ?? null,
    sizeBytes: raw.sizeBytes,
    createdBy: actorOf(raw.createdBy),
    createdAt: raw.createdAt,
  }
}

/** Qayerga yuklanadi: yetkazib beruvchi, xarid, to'lov dalili yoki yuk xati (omborchi) */
export type AttachmentTarget =
  | { owner: 'supplier'; supplierId: string }
  | { owner: 'purchase'; supplierId: string; purchaseId: string }
  | { owner: 'payment'; supplierId: string; purchaseId: string; paymentId: string }
  | { owner: 'receiving'; purchaseId: string }

export async function fetchSupplierAttachments(supplierId: string, signal?: AbortSignal): Promise<SupplierAttachment[]> {
  const response = await api.get<unknown>(`/api/finance/suppliers/${supplierId}/attachments`, {
    ...(signal ? { signal } : {}),
  })
  return z.array(attachmentSchema).parse(response.data).map(toAttachment)
}

function uploadPath(target: AttachmentTarget): string {
  switch (target.owner) {
    case 'supplier':
      return `/api/finance/suppliers/${target.supplierId}/attachments`
    case 'purchase':
      return `/api/finance/purchases/${target.purchaseId}/attachments`
    case 'payment':
      return `/api/finance/purchases/${target.purchaseId}/payments/${target.paymentId}/attachments`
    case 'receiving':
      return `/api/finance/purchase-receiving/${target.purchaseId}/attachments`
  }
}

export async function uploadSupplierAttachment(
  target: AttachmentTarget,
  kind: AttachmentKind | null,
  file: File,
): Promise<SupplierAttachment> {
  const form = new FormData()
  form.append('file', file)
  const response = await api.post<unknown>(uploadPath(target), form, {
    headers: { 'Content-Type': undefined },
    params: kind && (target.owner === 'supplier' || target.owner === 'purchase') ? { kind } : {},
  })
  return toAttachment(attachmentSchema.parse(response.data))
}

export async function supplierAttachmentUrl(supplierId: string, attachmentId: string): Promise<string> {
  const response = await api.get<unknown>(`/api/finance/suppliers/${supplierId}/attachments/${attachmentId}/url`)
  return z.object({ url: z.string() }).parse(response.data).url
}
